// Custom select dropdown — uses position:fixed to escape overflow:hidden parents
// Absolutely-positioned elements get clipped; fixed elements bypass all overflow constraints.
// Search uses an uncontrolled input to avoid React state timing issues with large option lists.
// Keyboard/ARIA (combobox + listbox) comes from useListboxNavigation.
import { useState, useRef, useEffect, forwardRef, useCallback } from 'react';
import type { SelectHTMLAttributes, CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { useListboxNavigation } from '../../hooks/useListboxNavigation';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange'> {
  error?: boolean;
  options: SelectOption[];
  placeholder?: string;
  onChange?: (e: { target: { value: string } }) => void;
}

// Max visible options — prevents thousands of DOM nodes from blocking the thread
const MAX_VISIBLE = 100;

export function normalizeSearchText(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

function getOptionMatchScore(option: SelectOption, normalizedQuery: string): number {
  if (!normalizedQuery) return 0;

  const normalizedLabel = normalizeSearchText(option.label);
  if (!normalizedLabel) return 0;

  const queryWords = normalizedQuery.split(' ');
  const labelWords = normalizedLabel.split(' ');
  const lengthPenalty = Math.max(0, normalizedLabel.length - normalizedQuery.length);

  if (normalizedLabel === normalizedQuery) return 1000 - lengthPenalty;
  if (labelWords.includes(normalizedQuery)) return 900 - lengthPenalty;
  if (normalizedLabel.startsWith(normalizedQuery)) return 800 - lengthPenalty;
  if (labelWords.some((word) => word.startsWith(normalizedQuery))) return 700 - lengthPenalty;
  if (queryWords.every((word) => labelWords.some((labelWord) => labelWord.startsWith(word)))) {
    return 600 - lengthPenalty;
  }
  if (queryWords.every((word) => normalizedLabel.includes(word))) return 500 - lengthPenalty;
  if (normalizedLabel.includes(normalizedQuery)) return 400 - lengthPenalty;

  return 0;
}

export function filterAndRankOptions(options: SelectOption[], searchQuery: string): SelectOption[] {
  const normalizedQuery = normalizeSearchText(searchQuery);
  if (!normalizedQuery) return options;

  return options
    .map((option, index) => ({
      option,
      index,
      score: getOptionMatchScore(option, normalizedQuery),
    }))
    .filter((entry) => entry.score > 0)
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return a.index - b.index;
    })
    .map((entry) => entry.option);
}

export const Select = forwardRef<HTMLButtonElement, SelectProps>(
  ({ error, options, placeholder, className = '', value, onChange, disabled, onBlur }, ref) => {
    const { t } = useTranslation('common');
    const [isOpen, setIsOpen] = useState(false);
    const [dropdownStyle, setDropdownStyle] = useState<CSSProperties>({});
    const [displayedOptions, setDisplayedOptions] = useState<SelectOption[]>([]);
    const [hiddenCount, setHiddenCount] = useState(0);

    const containerRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    // Uncontrolled input — browser owns the value, we read it on change
    const searchRef = useRef<HTMLInputElement>(null);

    // Forwarded ref (react-hook-form focuses it) + our own, to return focus after picking.
    const setTriggerRef = useCallback(
      (node: HTMLButtonElement | null) => {
        triggerRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      },
      [ref]
    );

    const selectedLabel = options.find((o) => o.value === value)?.label;

    // Apply search filter and update displayed options + overflow count
    const applyFilter = useCallback(
      (query: string) => {
        const all = filterAndRankOptions(options, query);
        setDisplayedOptions(all.slice(0, MAX_VISIBLE));
        setHiddenCount(Math.max(0, all.length - MAX_VISIBLE));
      },
      [options]
    );

    // Recompute fixed position from trigger rect every time dropdown opens
    useEffect(() => {
      if (!isOpen || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownStyle({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }, [isOpen]);

    // On open: initialize list and focus search. On close: reset input.
    useEffect(() => {
      if (!isOpen) {
        if (searchRef.current) searchRef.current.value = '';
        setDisplayedOptions([]);
        setHiddenCount(0);
        return;
      }
      // Re-apply current search term when options change while dropdown is open
      applyFilter(searchRef.current?.value ?? '');
      setTimeout(() => searchRef.current?.focus(), 0);
    }, [isOpen, applyFilter]);

    // Close on outside click — check both trigger and dropdown
    useEffect(() => {
      const handleOutsideClick = (e: MouseEvent) => {
        const target = e.target as Node;
        if (!containerRef.current?.contains(target) && !dropdownRef.current?.contains(target)) {
          setIsOpen(false);
        }
      };
      document.addEventListener('mousedown', handleOutsideClick);
      return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, []);

    // Closing from the keyboard/a pick returns focus to the trigger (the search input unmounts).
    const close = () => {
      setIsOpen(false);
      triggerRef.current?.focus();
    };

    const handleSelect = (optValue: string) => {
      onChange?.({ target: { value: optValue } });
      close();
    };

    const listbox = useListboxNavigation({
      items: displayedOptions,
      isOpen,
      onSelect: (opt) => handleSelect(opt.value),
      onClose: close,
      isItemDisabled: (opt) => !!opt.disabled,
      selectedIndex: displayedOptions.findIndex((o) => o.value === value),
    });

    const handleTriggerKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen((prev) => (e.key === 'ArrowDown' ? true : !prev));
      }
    };

    // Read value directly from DOM — avoids React state lag
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      applyFilter(e.target.value);
      listbox.resetActive();
    };

    return (
      <div ref={containerRef} className={`relative w-full ${className}`}>
        {/* Trigger button styled as a select input */}
        <button
          ref={setTriggerRef}
          type="button"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          disabled={disabled}
          onBlur={onBlur as unknown as React.FocusEventHandler<HTMLButtonElement>}
          onKeyDown={handleTriggerKeyDown}
          onClick={() => !disabled && setIsOpen((prev) => !prev)}
          className={`input w-full rounded-lg bg-[#3A3A3A] border border-white/10 focus:outline-none focus:border-white/20 text-left flex items-center justify-between ${error ? 'border-error' : ''} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <span className={`truncate ${!selectedLabel ? 'text-[#CACACA]' : 'text-[#F9F9F9]'}`}>
            {selectedLabel || placeholder || ''}
          </span>
        </button>

        {/* Fixed-position dropdown — escapes overflow:hidden without a portal */}
        {isOpen && !disabled && (
          <div
            ref={dropdownRef}
            style={dropdownStyle}
            className="fixed z-[9999] bg-base-100 border border-base-300 rounded-lg shadow-lg flex flex-col"
          >
            {/* Uncontrolled search input — browser owns the value, no React state lag */}
            <div className="p-2 border-b border-base-200">
              <input
                ref={searchRef}
                type="text"
                defaultValue=""
                {...listbox.getComboboxProps()}
                onChange={handleSearchChange}
                onMouseDown={(e) => e.stopPropagation()} // prevent outside-click handler
                placeholder={t('dropdown.searchPlaceholder')}
                className="input input-sm input-bordered w-full"
              />
            </div>

            {/* Empty state lives outside the listbox (only options may be its children) */}
            {displayedOptions.length === 0 && (
              <p className="px-4 py-2 text-sm text-base-content/40 cursor-default select-none">
                {t('common.noResults')}
              </p>
            )}

            {/* Scrollable options list */}
            <ul {...listbox.getListboxProps(placeholder)} className="max-h-52 overflow-y-auto">
              {displayedOptions.map((opt, index) => (
                <li
                  key={opt.value}
                  {...listbox.getOptionProps(index, value === opt.value)}
                  onMouseDown={(e) => e.preventDefault()} // prevent blur before click registers
                  onClick={() => !opt.disabled && handleSelect(opt.value)}
                  className={`px-4 py-2 text-sm cursor-pointer hover:bg-base-200 ${
                    opt.disabled ? 'opacity-40 cursor-not-allowed' : ''
                  } ${listbox.isActive(index) ? 'bg-base-200' : ''} ${
                    value === opt.value ? 'bg-primary/10 font-medium' : ''
                  }`}
                >
                  {opt.label}
                </li>
              ))}
            </ul>

            {/* Overflow hint — shown when results are capped at MAX_VISIBLE */}
            {hiddenCount > 0 && (
              <div className="px-4 py-1.5 text-xs text-base-content/40 border-t border-base-200 select-none">
                {searchRef.current?.value
                  ? t('dropdown.moreMatches', { count: hiddenCount })
                  : t('dropdown.moreOptions', { count: hiddenCount })}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';
